<div id="main" class="w-full h-full flex flex-col flex-1 min-h-0">
  <rapider-page-header
    title="Inbox"
    description="Your work, decisions and updates. All in one place."
    [primaryAction]="{ text: '＋ New work' }"
    [pills]="headerPills()"
    [selectedPillKey]="selectedKindFilter()"
    [selectOptions]="headerSelectOptions"
    [selectedSelectOption]="selectedAppFilter()"
    (pillClick)="setKindFilter($event)"
    (selectChange)="setAppFilter($event)"
  >
    <div header-actions>
      <button
        nz-dropdown
        [nzDropdownMenu]="homeMenu"
        [nzPlacement]="'bottomRight'"
        class="w-[38px] h-[38px] rounded-md flex items-center justify-center text-secondary-text border border-transparent hover:bg-hover-default hover:text-primary transition-colors focus:outline-none"
      >
        <i class="fas fa-ellipsis-v"></i>
      </button>
      <nz-dropdown-menu #homeMenu="nzDropdownMenu">
        <ul
          nz-menu
          class="bg-component-inner border border-primary-border shadow-thick rounded-lg min-w-[200px] overflow-hidden p-1"
        >
          <li
            nz-menu-item
            class="cursor-pointer text-sm font-medium transition-colors hover:bg-hover-default text-primary-text rounded-md"
            (click)="generateMockData()"
          >
            <div class="flex items-center gap-2 px-1 py-1">
              <i class="fas fa-database text-secondary-text"></i>
              <span>Seed Demo Data</span>
            </div>
          </li>
        </ul>
      </nz-dropdown-menu>
    </div>
  </rapider-page-header>

  <div class="flex flex-col w-full @container flex-1 min-h-0">
    <div class="inbox w-full flex-1 min-h-0">
      <section class="listcol" aria-label="Work item inbox">
        <div class="list-controls">
          <select id="viewFilter" aria-label="Inbox view">
            <option value="active">Inbox</option>
            <option value="unread">Unread</option>
            <option value="all">All states</option>
          </select>
          <select id="sortOrder" aria-label="Sort items">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </div>
        <div class="search">
          <input
            id="search"
            aria-label="Search work items"
            placeholder="Search items or applications…"
          />
        </div>

        @if (isLoading()) {
          <div class="flex justify-center p-4">
            <i
              class="fas fa-circle-notch fa-spin"
              style="color: var(--blue)"
            ></i>
          </div>
        } @else if (workItems().length === 0) {
          <div class="empty">
            <h3>No matching items</h3>
            <p>Try another application or work type.</p>
          </div>
        } @else {
          @for (item of workItems(); track item.id) {
            <button
              class="item"
              [ngClass]="{ selected: selectedItem()?.id === item.id }"
              (click)="selectItem(item)"
            >
              <div class="itemline">
                <span style="text-transform: uppercase">{{
                  getApplicationId(item).replace('-app', '')
                }}</span>
                <time>{{ item.occurredAt | date: 'shortTime' }}</time>
              </div>
              <b>{{ item.title }}</b>
              <p>{{ item.bodyMarkdown | slice: 0 : 80 }}...</p>
              <div class="itemfoot">
                <span
                  class="pill"
                  [ngClass]="{
                    amber: item.kind === 'approval',
                    blue: item.kind === 'task',
                  }"
                  >{{ item.kind }}</span
                >
                @if (item.needsAttention) {
                  <span class="pill red">Needs attention</span>
                }
                @if (item.status && item.status !== 'pending') {
                  <span class="pill green" style="text-transform: capitalize">{{
                    item.status
                  }}</span>
                }
              </div>
            </button>
          }
        }
      </section>

      <article class="reader" id="reader">
        @if (selectedItem(); as item) {
          <div class="reader-tools">
            <div>
              <button class="quiet">Archive</button>
              <button class="quiet">Later</button>
            </div>
            <div>
              <button class="quiet">Item history</button>
              <button class="quiet" (click)="saveToNotebook()">
                Save to notebook ↗
              </button>
            </div>
          </div>

          <div class="body">
            <div class="eyebrow">
              <span class="appmark">▣</span>
              <span>
                <b style="color: #445169; text-transform: uppercase">{{
                  getApplicationId(item).replace('-app', '')
                }}</b>
                · <span style="text-transform: capitalize">{{ item.kind }}</span
                ><br />
                <span class="received">{{
                  item.occurredAt | date: 'medium'
                }}</span>
              </span>
            </div>

            <h2>{{ item.title }}</h2>

            <div class="meta">
              <span class="pill blue" style="text-transform: capitalize">{{
                item.status || 'Open'
              }}</span>
              <button
                class="quiet small"
                style="padding: 1px 3px; color: #687c9b"
                (click)="navigateToSource()"
              >
                Open Source ↗
              </button>
            </div>

            <div class="markdown">
              <pre
                style="white-space: pre-wrap; font-family: inherit; margin: 0"
                >{{ item.bodyMarkdown }}</pre
              >
            </div>

            @if (item.recommendation) {
              <div class="recommendation">
                <strong>✧ AI RECOMMENDATION</strong>
                <p>{{ $any(item.recommendation).text }}</p>
              </div>
            }

            @if (item.kind === 'approval') {
              <div class="approval-intro" style="margin-top: 24px">
                Approval Required
              </div>
              <div class="proposal" style="margin-top: 8px">
                <div class="cardtop">
                  <b>Action Request</b>
                  @if (item.status === 'pending' || !item.status) {
                    <span class="pill amber">Awaiting approval</span>
                  } @else if (item.status === 'completed') {
                    <span class="pill green">Completed</span>
                  }
                </div>
                <p>Review the request before taking action.</p>

                <div class="actions">
                  @if (item.status === 'pending' || !item.status) {
                    <button class="primary" (click)="handleAction('approve')">
                      Approve & run
                    </button>
                    <button class="quiet" (click)="handleAction('reject')">
                      Reject
                    </button>
                  } @else if (item.status === 'running') {
                    <div
                      class="inline-job"
                      style="width: 100%; margin: 0; padding: 12px 16px"
                    >
                      <b style="font-size: 12px">Action in progress</b>
                      <div class="progress">
                        <span style="width: 45%"></span>
                      </div>
                      <p>Processing action...</p>
                    </div>
                  } @else {
                    <div
                      class="inline-job"
                      style="width: 100%; margin: 0; padding: 12px 16px"
                    >
                      <b style="font-size: 12px; color: var(--green)"
                        >✓ Result ready</b
                      >
                      <div class="progress">
                        <span
                          style="width: 100%; background: var(--green)"
                        ></span>
                      </div>
                      <p>Action {{ item.status }}</p>
                    </div>
                  }
                </div>
              </div>
            }
          </div>

          <section class="conversation" aria-label="AI conversation">
            <div class="threadhead">
              <b>✧ Contextual AI Assistant</b>
              <select aria-label="Choose a conversation">
                <option value="new">New thread</option>
              </select>
            </div>
            <div class="composer">
              <textarea
                placeholder="Ask a question about this item, or draft a response..."
              ></textarea>
              <div class="compose-bottom">
                <span class="context-chip">↗ This item context</span>
                <button class="primary">Send ↑</button>
              </div>
            </div>
            <p class="thread-note">
              AI can assist with understanding or completing this item.
            </p>
          </section>
        } @else {
          <div class="reader-empty">
            <span class="appmark">▣</span>
            <h2>A little room to focus</h2>
            <p>
              Select a work item, adjust your filters, or create something new.
            </p>
          </div>
        }
      </article>
    </div>
  </div>
</div>
